import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { DASHBOARD } from "@/constants/testIds";
import {
  Play, ArrowRight, ClipboardEdit, Trophy, BadgeCheck, Sparkles,
  CheckCircle2, Award, BookOpenCheck, MessagesSquare, Lock, Scale,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import FirstWeekPreview from "@/components/FirstWeekPreview";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import UpgradeOfferBanner from "@/components/UpgradeOfferBanner";
import PendingUpgradeBanner from "@/components/PendingUpgradeBanner";
import ReferralGateBanner from "@/components/ReferralGateBanner";
import WelcomeUpgradeModal from "@/components/WelcomeUpgradeModal";
import DiscordLinkBanner from "@/components/DiscordLinkBanner";
import DiscordPulse from "@/components/DiscordPulse";
import SessionsClock from "@/components/SessionsClock";
import SummitGraduateProgress from "@/components/SummitGraduateProgress";
import AchievementsWidget from "@/components/AchievementsWidget";
import LearningStreakCard from "@/components/LearningStreakCard";
import CourseProgress from "@/components/CourseProgress";
import { DashboardFollowUs } from "@/components/DashboardFollowUs";

export default function Dashboard() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [articles, setArticles] = useState([]);
  const [progress, setProgress] = useState([]);
  const [graderStats, setGraderStats] = useState(null);
  const [sidebarStats, setSidebarStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/courses?language=${lang}`).then((r) => setCourses(r.data)).catch(() => {}),
      api.get(`/articles?language=${lang}`).then((r) => setArticles(r.data.slice(0, 4))).catch(() => {}),
      api.get("/progress").then((r) => setProgress(r.data)).catch(() => {}),
      api.get("/grader/monthly-stats").then((r) => setGraderStats(r.data)).catch(() => {}),
      api.get("/dashboard/sidebar-stats").then((r) => setSidebarStats(r.data)).catch(() => {}),
    ]);
  }, [lang]);

  const featuredCourse = courses[0];
  const nextCourse = courses[1];

  return (
    <div data-testid={DASHBOARD.root} className="space-y-8">
      {/* Invisible unless the user just upgraded — modal, sits above content */}
      <WelcomeUpgradeModal user={user} />

      <SessionsClock />

      {/*
        Content order matches the finalised information hierarchy:
          1. Onboarding checklist (only until the user finishes or dismisses it — auto-hides on 100%)
          2. Actionable banners (upgrade / discord link / referral) — deliberately grouped so users see
             the pending actions BEFORE the standing greeting.
          3. Greeting header ("Good to see you, {name}").
          4. Achievements — showcase what they've earned.
          5. Archetype pill ("Here's where you left off" — sample roadmap the Professor mapped).
          6. Course Progress bars (NEW) — every started course with a % bar and Resume link.
          7. Continue Learning + Up Next — the flagship "resume playback" cards.
          8. Certificate progress — Summit Graduate arc.
          9. Free-tier first-week preview + articles list stay at the bottom (top-of-funnel content).
        The LIVE banner is above this whole section via DashboardShell — no wiring needed here.
      */}

      <OnboardingChecklist user={user} />

      {/* Transactional banners cluster — order tuned for urgency. */}
      <PendingUpgradeBanner user={user} />
      <ReferralGateBanner />
      <DiscordLinkBanner user={user} />
      <UpgradeOfferBanner user={user} />

      {/* 3. Greeting */}
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
          — {t("nav.dashboard")}
        </div>
        <h1
          data-testid={DASHBOARD.greeting}
          className="font-display font-bold text-4xl text-slate-900 tracking-tight"
        >
          {t("dashboardUi.greeting", { name: user?.name?.split(" ")[0] || t("dashboardUi.greetingFallback") })}
        </h1>
        <p className="mt-2 text-slate-600">{t("dashboardUi.leftOff")}</p>
      </div>

      <DiscordPulse user={user} />

      {/* 4. Achievements */}
      <LearningStreakCard />
      <AchievementsWidget />

      {/* 5. "Here's where you left off" — the personalised archetype/roadmap card */}
      {user?.onboarding?.archetype && (
        <div
          data-testid="dashboard-archetype-pill"
          className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white p-6 sm:p-8"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:items-start">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-amber-300 font-bold">
                {t("dashboardUi.yourArchetype")}
              </div>
              <div className="mt-3 font-display font-black text-2xl sm:text-3xl text-white tracking-tight">
                {user.onboarding.archetype}
              </div>

              {Array.isArray(user.onboarding.roadmap) && user.onboarding.roadmap.length > 0 ? (
                <>
                  <div className="mt-5 text-[10px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
                    {t("dashboardUi.sampleRoadmap")}
                  </div>
                  <ol className="mt-3 relative border-l-2 border-amber-500/30 ml-2 pl-5 space-y-3">
                    {user.onboarding.roadmap.map((step, i) => (
                      <li key={i} className="relative">
                        <span className="absolute -left-[27px] top-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-slate-950 font-mono font-black text-[10px]">
                          {i + 1}
                        </span>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">
                          {step.when}
                        </div>
                        <div className="text-sm font-semibold text-white leading-snug">
                          {step.title}
                        </div>
                      </li>
                    ))}
                  </ol>
                </>
              ) : (
                <>
                  <div className="mt-4 text-[11px] uppercase tracking-[0.24em] text-slate-400 font-semibold">
                    {t("dashboardUi.continueFrom")}
                  </div>
                  <div className="text-sm sm:text-base text-slate-200 font-semibold">
                    {user.onboarding.starting_chapter}
                  </div>
                </>
              )}
            </div>
            <div className="shrink-0 lg:pt-1">
              <Link
                to="/courses"
                data-testid="dashboard-archetype-continue"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm h-11 px-5 rounded-lg transition-colors"
              >
                {t("dashboardUi.startStep1")} <span aria-hidden>→</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* 6. Course Progress (NEW) — one row per started course with a % bar. */}
      <CourseProgress />

      {/* Metrics — lessons finished, journal entries, avg grade, tier */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          testId={DASHBOARD.metricLessons}
          icon={<BadgeCheck size={18} className="text-blue-700" />}
          label={t("dashboardUi.metrics.lessons")}
          value={progress.length}
          note={t("dashboardUi.metrics.lessonsNote")}
          href="/courses"
        />
        <MetricCard
          testId="dashboard-metric-journal"
          icon={<ClipboardEdit size={18} className="text-blue-700" />}
          label={t("dashboardUi.metrics.journal")}
          value={sidebarStats?.resources?.journal_entries_30d ?? 0}
          note={t("dashboardUi.metrics.journalNote")}
          href="/resources?tab=journal"
        />
        <GradeMetricCard stats={graderStats} tier={user?.tier || "free"} />
        <MetricCard
          testId={DASHBOARD.metricTier}
          icon={<Trophy size={18} className="text-blue-700" />}
          label={t("dashboardUi.metrics.tier")}
          value={<span className="capitalize">{t(`tier.${user?.tier || "free"}`)}</span>}
          note={user?.tier === "premium" ? t("dashboardUi.metrics.tierNoteAllAccess") : t("dashboardUi.metrics.tierNoteUpgrade")}
          href="/pricing"
        />
      </div>

      {/* 7. Continue Learning + Up next */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div
          data-testid={DASHBOARD.continueCard}
          className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
        >
          {featuredCourse && (
            <div className="relative aspect-[16/8]">
              <img
                src={featuredCourse.cover_image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="text-xs uppercase tracking-widest text-blue-300 font-semibold mb-2">
                  {t("dashboardUi.continueEyebrow", { level: featuredCourse.level })}
                </div>
                <h3 className="font-display font-bold text-3xl">{featuredCourse.title}</h3>
                <p className="mt-2 text-slate-200 max-w-xl text-sm">{featuredCourse.summary}</p>
                <Link
                  to={`/courses/${featuredCourse.id}`}
                  className="mt-6 inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-md font-medium text-sm hover:bg-blue-500 hover:text-slate-900 transition-colors"
                >
                  <Play size={14} fill="currentColor" /> {t("dashboardUi.resume")}
                </Link>
              </div>
            </div>
          )}
        </div>

        {nextCourse && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow">
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">
              {t("dashboardUi.upNext")}
            </div>
            <h3 className="mt-3 font-display font-bold text-xl text-slate-900">
              {nextCourse.title}
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              {nextCourse.summary}
            </p>
            <div className="mt-4 text-xs text-slate-500 uppercase tracking-wider">
              {t("dashboardUi.levelDuration", { level: nextCourse.level, duration: nextCourse.duration })}
            </div>
            <Link
              to={`/courses/${nextCourse.id}`}
              className="mt-6 inline-flex items-center gap-1 text-blue-800 font-semibold text-sm hover:gap-2 transition-all"
            >
              {t("dashboardUi.startCourse")} <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>

      {/* 8. Certificate progress (Summit Graduate arc). */}
      <SummitGraduateProgress user={user} />

      {/* Free-tier funnel: "Your first week as a member" preview */}
      {user?.tier === "free" && <FirstWeekPreview />}

      <DashboardFollowUs />

      {/* Articles list */}
      <div className="bg-white border border-slate-200 rounded-2xl p-8">
        <div className="flex items-baseline justify-between mb-6">
          <h3 className="font-display font-bold text-2xl text-slate-900">{t("dashboardUi.recommendedReading")}</h3>
          <Link to="/articles" className="text-sm text-blue-800 font-semibold hover:underline">
            {t("dashboardUi.allArticles")}
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {articles.map((a) => (
            <Link
              key={a.id}
              to={`/articles/${a.id}`}
              className="group flex gap-4 hover:bg-slate-50 rounded-lg p-3 -m-3 transition-colors"
            >
              <img
                src={a.cover_image}
                alt=""
                className="w-24 h-24 rounded-lg object-cover shrink-0"
              />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-blue-800 font-semibold">
                  {t("dashboardUi.categoryReadTime", { category: a.category, readTime: a.read_time })}
                </div>
                <div className="mt-1 font-display font-semibold text-slate-900 leading-snug group-hover:text-blue-800">
                  {a.title}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, note, testId, href }) {
  const Wrapper = href ? Link : "div";
  const wrapperProps = href
    ? { to: href, className: "block bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-sm transition-all group" }
    : { className: "bg-white border border-slate-200 rounded-2xl p-6" };
  return (
    <Wrapper data-testid={testId} {...wrapperProps}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
        {icon} <span className={href ? "group-hover:text-blue-800 transition-colors" : ""}>{label}</span>
      </div>
      <div className="mt-4 font-display font-black text-3xl text-slate-900">{value}</div>
      <div className="mt-1 text-xs text-slate-500 flex items-center gap-1">
        {note}
        {href && <ArrowRight size={11} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
      </div>
    </Wrapper>
  );
}

const GRADE_TILE_COLORS = {
  A: { bg: "bg-emerald-500", text: "text-emerald-600" },
  B: { bg: "bg-blue-500",    text: "text-blue-600"    },
  C: { bg: "bg-amber-500",   text: "text-amber-600"   },
  D: { bg: "bg-orange-500",  text: "text-orange-600"  },
  F: { bg: "bg-rose-500",    text: "text-rose-600"    },
};

function GradeDistribution({ distribution }) {
  const { t } = useTranslation();
  const grades = ["A", "B", "C", "D", "F"];
  const max = Math.max(...grades.map((g) => distribution?.[g] || 0), 1);
  return (
    <div className="mt-4 flex items-end justify-between gap-1.5" aria-label={t("dashboardUi.gradeDistributionLabel")}>
      {grades.map((g) => {
        const count = distribution?.[g] || 0;
        const color = GRADE_TILE_COLORS[g].bg;
        // Give even the zero-count bars a tiny nub so the axis reads as a chart.
        const heightPct = count === 0 ? 8 : Math.max(18, (count / max) * 100);
        return (
          <div key={g} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div className="w-full h-10 flex items-end">
              <div
                className={`w-full rounded-t-md transition-all ${count === 0 ? "bg-slate-200" : color}`}
                style={{ height: `${heightPct}%` }}
                title={`${count} × ${g}`}
              />
            </div>
            <div className="text-[9px] font-mono font-bold text-slate-500 tabular-nums">
              {g}<span className="text-slate-400 ml-0.5">{count}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function GradeMetricCard({ stats, tier }) {
  const { t } = useTranslation();
  const hasData = stats && stats.count > 0;
  const grade = stats?.avg_grade || "C";
  const style = GRADE_TILE_COLORS[grade] || GRADE_TILE_COLORS.C;
  const label = t(`dashboardUi.gradeMetric.labels.${grade}`);

  // Nudge Basic members toward Premium once a C-or-worse average shows up.
  const nudgeUpsell = hasData && tier === "basic" && ["C", "D", "F"].includes(grade);

  return (
    <div
      data-testid={DASHBOARD.metricGrade}
      className="bg-white border border-slate-200 rounded-2xl p-6"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
        <Scale size={16} className="text-blue-700" />
        {t("dashboardUi.gradeMetric.title")}
      </div>

      {hasData ? (
        <>
          <div className="mt-4 flex items-baseline gap-3">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-display font-black text-3xl text-white ${style.bg}`}>
              {grade}
            </div>
            <div>
              <div className={`text-xs font-semibold uppercase tracking-widest ${style.text}`}>
                {label}
              </div>
              <div className="font-mono font-bold text-sm text-slate-700 mt-0.5">
                {t("dashboardUi.gradeMetric.score", { score: stats.avg_score, count: stats.count })}
              </div>
            </div>
          </div>
          <GradeDistribution distribution={stats.distribution} total={stats.count} />
          {nudgeUpsell ? (
            <Link
              to="/pricing?src=grade-nudge"
              className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-800 hover:text-blue-700"
            >
              {t("dashboardUi.gradeMetric.professorCanHelp")} <ArrowRight size={11} />
            </Link>
          ) : (
            <div className="mt-3 text-xs text-slate-500">
              <Link to="/resources?tab=grader" className="text-blue-800 font-semibold hover:underline">
                {t("dashboardUi.gradeMetric.gradeAnother")} →
              </Link>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="mt-4 font-display font-black text-3xl text-slate-400">—</div>
          <div className="mt-1 text-xs text-slate-500">
            <Link to="/resources?tab=grader" className="text-blue-800 font-semibold hover:underline">
              {t("dashboardUi.gradeMetric.gradeFirst")}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
